import React, { useState, useEffect } from 'react';
import { getDoc, doc } from 'firebase/firestore';
import { firestore } from '~/firebase/init';
import { useDisclosure } from '@chakra-ui/react';

import cartStatusAtom from '~/recoil/atom/cartStatus';
import memberInfoAtom from '~/recoil/atom/memberInfo';
import authStatusAtom from '~/recoil/atom/authStatus';
import useGroupCart from '~/hooks/useGroupCart';
import ordersAtom from '~/recoil/atom/orders';
import authAtom from '~/recoil/atom/auth';
import uidAtom from '~/recoil/atom/uid';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { useParams, useNavigate } from 'react-router-dom';

import MemberInfoModal from '~/components/MemberInfoModal';
import UnableGroupNoticeModal from '~/components/UnableGroupNoticeModal';

import updateGroupCart from '~/firebase/update/updateGroupCart';

import * as fp from 'lodash/fp';
import { FirebaseDataStatus } from '~/types/utilities';
import { CartStatus, StoreOrderInCart } from '~/types/cart';
import { PLATFORM_ID } from '~/globalVariables';

export default function GroupBuyProvider({ children }: { children?: React.ReactElement | null }) {
  const navigate = useNavigate();
  const { groupId = '' } = useParams();
  const { groupCartStatus } = useGroupCart(groupId);

  const authStatus = useRecoilValue(authStatusAtom);
  const auth = useRecoilValue(authAtom);
  const uid = useRecoilValue(uidAtom); // cart uid
  const cartStatus = useRecoilValue(cartStatusAtom);
  const orders = useRecoilValue(ordersAtom);

  const setMemberInfo = useSetRecoilState(memberInfoAtom);

  const { isOpen, onClose, onOpen } = useDisclosure();
  const {
    isOpen: isGroupNoticeModalOpen,
    onClose: onGroupNoticeModalClose,
    onOpen: onGroupNoticeModalOpen,
  } = useDisclosure();

  const handleInitMemberInfo = async (id: string, name: string) => {
    const localStorageId = localStorage.getItem('memberId') || '';
    const ref = doc(firestore, 'platforms', PLATFORM_ID, 'carts', groupId);
    const snap = await getDoc(ref);

    const newOrders = replaceOrdersMemberInfo(snap.data()?.orders || [], localStorageId, {
      memberId: id,
      memberName: name,
    });

    setMemberInfo({
      name: name,
      id: id,
    });

    try {
      const res = await updateGroupCart(PLATFORM_ID, groupId, {
        orders: newOrders,
      });
    } catch (err) {}
  };

  const handleConfirm = async (id: string, name: string) => {
    try {
      const res = await handleInitMemberInfo(id, name);
      onClose();
    } catch (err) {}
  };

  useEffect(() => {
    if (
      fp.eq(authStatus, FirebaseDataStatus.INIT) ||
      fp.eq(groupCartStatus, FirebaseDataStatus.INIT)
    )
      return;

    if (fp.eq(groupCartStatus, FirebaseDataStatus.NOT_EXIST)) {
      navigate('/');
      return;
    }

    if (!auth) {
      setMemberInfo({
        name: localStorage.getItem('memberName') || '',
        id: localStorage.getItem('memberId') || '',
      });
      onOpen();
      return;
    }
    if (fp.eq(auth.uid, uid)) {
      handleInitMemberInfo(auth.uid, auth.displayName || '');
      onClose();
      return;
    }

    setMemberInfo({
      name: auth.displayName || '',
      id: auth.uid,
    });
    onOpen();
  }, [authStatus, groupCartStatus, auth, uid]);

  useEffect(() => {
    if ([CartStatus.CANCELED, CartStatus.ORDERED].includes(cartStatus)) {
      onGroupNoticeModalOpen();
    }
  }, [cartStatus]);

  return (
    <>
      {children}

      <>
        <MemberInfoModal isOpen={isOpen} onClose={onClose} onConfirm={handleConfirm} />
        <UnableGroupNoticeModal isOpen={isGroupNoticeModalOpen} cartStatus={cartStatus} />
      </>
    </>
  );
}

function replaceOrdersMemberInfo(
  orders: Array<StoreOrderInCart>,
  originId: string,
  replaceMember: { memberId: string; memberName: string },
): Array<StoreOrderInCart> {
  return orders.map((order) => {
    const userGroupItem = order.groupItems.find(fp.propEq('memberId', replaceMember.memberId));
    const originGroupItem = order.groupItems.find(fp.propEq('memberId', originId));

    const newUserGroupItem = {
      memberId: replaceMember.memberId,
      memberName: replaceMember.memberName,
      items: (userGroupItem?.items || []).concat(
        fp.eq(originId, replaceMember.memberId) ? [] : originGroupItem?.items || [],
      ),
    };

    const newGroupItems = fp
      .cond([
        [
          fp.where<Partial<{ userExist: boolean; originExist: boolean }>>({
            userExist: fp.identity,
          }),
          // 有該使用者的
          () => {
            const updatedGroupItems = order.groupItems.map((groupItem) =>
              fp.eq(groupItem.memberId, replaceMember.memberId) ? newUserGroupItem : groupItem,
            );

            return fp.eq(originId, replaceMember.memberId)
              ? updatedGroupItems
              : updatedGroupItems.filter((groupItem) => !fp.eq(groupItem.memberId, originId));
          },
        ],
        [
          fp.T,
          fp.always(
            order.groupItems
              .filter(({ memberId }) => !fp.eq(memberId, originId))
              .concat([newUserGroupItem]),
          ),
        ],
      ])({ userExist: !!userGroupItem, originExist: !!originGroupItem })
      .filter(({ items }) => fp.gt(items.length, 0));

    return { ...order, groupItems: newGroupItems };
  });
}
